import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Modal } from 'antd';
import { addProjectToWorkspace, canEdit, removeProjectFromWorkspace } from '../../services/WorkspacesService';

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'en', { sensitivity: 'base' });

/**
 * The workspaces the picker offers: the ones the person owns or edits (they may add to them), and the ones the project
 * is already in (its owner may take it out of any of them), by name.
 */
export function pickerTargets(workspaces, current) {
  const inIds = new Set((current || []).map((workspace) => workspace.id));
  return (workspaces || []).filter((workspace) => canEdit(workspace) || inIds.has(workspace.id)).sort(byName);
}

/** What saving does: the workspaces to add the project to and the ones to take it out of. */
export function pickerChanges(current, chosen) {
  const before = new Set((current || []).map((workspace) => workspace.id));
  const after = new Set(chosen);
  return { add: [...after].filter((id) => !before.has(id)), remove: [...before].filter((id) => !after.has(id)) };
}

/**
 * "Add to workspace…" for one of the person's own projects: a checkbox per workspace they own or edit (a project may be
 * in several), the ones it is in already ticked; unticking takes it out of that one only. The project's owner, its
 * sharing and who may open it through other workspaces do not change; a workspace's members reach it while it is in.
 * Saving adds and removes one workspace at a time; `onSaved({ added, removed, failed })` once the gateway answered, a
 * refusal is shown and the dialog stays open.
 */
export default function AddToWorkspaceDialog({ open, project, workspaces, current, onClose, onSaved, loading = false }) {
  const [chosen, setChosen] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const currentKey = (current || []).map((workspace) => workspace.id).join('|');

  useEffect(() => {
    if (open) {
      setChosen(currentKey ? currentKey.split('|') : []);
      setError(null);
    }
  }, [open, currentKey]);

  const targets = pickerTargets(workspaces, current);
  const { add, remove } = pickerChanges(current, chosen);
  const unchanged = add.length === 0 && remove.length === 0;
  const nameOf = (id) => workspaces.find((workspace) => workspace.id === id)?.name || (current || []).find((workspace) => workspace.id === id)?.name || id;
  const toggle = (id) => setChosen((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]));

  const save = async() => {
    setSaving(true);
    setError(null);
    const added = [];
    const removed = [];
    const failed = [];
    for (const [ids, act, done] of [[add, addProjectToWorkspace, added], [remove, removeProjectFromWorkspace, removed]]) {
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await act(id, project.id);
          done.push(nameOf(id));
        } catch (failure) {
          failed.push(`${nameOf(id)}: ${failure.message || 'refused'}`);
        }
      }
    }
    setSaving(false);
    if (failed.length) {
      setError(failed.join(' · '));
      if (added.length || removed.length) { onSaved({ added, removed, failed, keepOpen: true }); }
      return;
    }
    onSaved({ added, removed, failed });
  };

  return (
    <Modal
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button disabled={unchanged || saving} key="save" loading={saving} onClick={save} type="primary">Save</Button>,
      ]}
      onCancel={onClose}
      open={open}
      title={`Add “${project?.name || 'project'}” to workspaces`}
    >
      <p className="workspace-dialog-hint">
        A project can be in several workspaces. Everyone in a workspace can reach the project; you stay its owner, and the
        people you shared it with directly keep their access.
      </p>
      {loading && targets.length === 0 && <p className="workspace-dialog-empty">Loading your workspaces…</p>}
      {targets.length === 0 ? (
        !loading && <p className="workspace-dialog-empty">You have no workspace to add it to yet. Create one on the Workspaces page.</p>
      ) : (
        <fieldset className="workspace-dialog-choices">
          <legend className="workspace-dialog-legend">Workspaces</legend>
          {targets.map((workspace) => (
            <Checkbox checked={chosen.includes(workspace.id)} key={workspace.id} onChange={() => toggle(workspace.id)}>
              {workspace.name}
              <span className="workspace-dialog-role"> · {workspace.role}</span>
            </Checkbox>
          ))}
        </fieldset>
      )}
      {error && <p className="workspace-dialog-error" role="alert">{error}</p>}
    </Modal>
  );
}

AddToWorkspaceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  project: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  workspaces: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  current: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
